import styled from '@emotion/styled';
import { Link } from 'react-router-dom';

const Container = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background-color: #fff;
  color: #000;
  /* Mobile-first: compact padding on small screens */
  padding: 2rem 1rem;
  padding-top: max(2rem, env(safe-area-inset-top));
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 2;

  @media (min-width: 640px) {
    padding: 4rem 1.25rem;
  }

  @media (min-width: 768px) {
    padding: 6rem 2rem;
  }

  @media (min-width: 1200px) {
    padding: 8rem 2rem;
  }
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 2rem;
  max-width: 800px;
  padding: 0 0.5rem;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 640px) {
    margin-bottom: 3rem;
  }

  @media (min-width: 768px) {
    margin-bottom: 6rem;
    padding: 0;
  }

  @media (min-width: 1200px) {
    margin-bottom: 8rem;
  }
`;

const Title = styled.h1`
  /* Mobile-first: smaller base, scales up */
  font-size: 2rem;
  font-weight: 400;
  margin-bottom: 1rem;
  text-align: center;
  letter-spacing: -0.03em;

  @media (min-width: 640px) {
    font-size: 2.75rem;
  }

  @media (min-width: 768px) {
    font-size: 3.5rem;
  }

  @media (min-width: 1200px) {
    font-size: 4rem;
    margin-bottom: 1.5rem;
  }
`;

const Subtitle = styled.h2`
  font-size: 1rem;
  font-weight: 300;
  color: #666;
  margin-bottom: 1.5rem;
  line-height: 1.6;
  padding: 0 0.5rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (min-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 2rem;
    padding: 0;
  }
`;

const ExhibitionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  width: 100%;
  padding: 0 0.5rem;

  @media (min-width: 480px) {
    gap: 2rem;
  }

  @media (min-width: 640px) {
    padding: 0 1rem;
  }

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 3rem;
    padding: 0 2rem;
  }

  @media (min-width: 1200px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 4rem;
    padding: 0;
  }
`;

const ExhibitionCard = styled(Link)`
  text-decoration: none;
  color: inherit;
  background: #fff;
  border: 1px solid #eee;
  border-radius: 8px;
  overflow: hidden;
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  /* Touch-friendly: entire card is tappable */
  min-height: 0;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
  }

  /* Mobile: active state for touch feedback */
  &:active {
    opacity: 0.95;
  }
`;

const ExhibitionImage = styled.img`
  width: 100%;
  /* Mobile-first: smaller height, scales up */
  height: 220px;
  object-fit: cover;
  display: block;

  @media (min-width: 480px) {
    height: 260px;
  }

  @media (min-width: 768px) {
    height: 300px;
  }
`;

const ExhibitionInfo = styled.div`
  padding: 1rem;
  flex: 1;
  display: flex;
  flex-direction: column;

  @media (min-width: 768px) {
    padding: 1.5rem;
  }
`;

const ExhibitionTitle = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  line-height: 1.3;

  @media (min-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
  }
`;

const ExhibitionDescription = styled.p`
  color: #666;
  font-size: 0.9rem;
  line-height: 1.6;
  flex: 1;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const exhibitions = [
  {
    id: 'undo-redo',
    title: 'Undo Redo',
    description: 'Exploring fragile states of memory, choice, and alternate timelines',
    image: '/images/undo-redo/cover.jpg',
    imageHomepage: '/images/homepage/undo-redo.jpg',
    path: '/exhibitions/undo-redo'
  },
  {
    id: 'in-outscape',
    title: 'In : Outscape',
    description: 'A dual exhibition exploring internal landscapes and outward expressions',
    image: '/images/InOutscape/Kotaro Yokomizo/uramichi8.jpeg',
    imageHomepage: '/images/homepage/in-outscape.jpg',
    path: '/exhibitions/in-outscape'
  },
  {
    id: 'mutating-wonders',
    title: 'Mutating Wonders',
    description: 'A solo exhibition exploring the thematic thread of Seeds, and its fragile state of in-between stages.',
    image: '/images/cover_MutatingWonders.jpg',
    imageHomepage: '/images/homepage/mutating-wonders.jpg',
    path: '/exhibitions/mutating-wonders'
  },
  {
    id: 'the-smoke',
    title: 'The Smoke',
    description: 'Exploring the theme of Burning season and how it affects our physical and emotional health',
    image: '/images/suzanna-hazy-days.jpg',
    imageHomepage: '/images/homepage/the-smoke.jpg',
    path: '/exhibitions/the-smoke'
  }
];

const Exhibitions = () => {
  return (
    <Container>
      <ContentWrapper>
        <Header>
          <Title>Exhibitions</Title>
          <Subtitle>Explore our current and upcoming exhibitions</Subtitle>
        </Header>
        <ExhibitionGrid>
          {exhibitions.map(exhibition => (
            <ExhibitionCard key={exhibition.id} to={exhibition.path}>
              <ExhibitionImage
                src={exhibition.imageHomepage ?? exhibition.image}
                alt={exhibition.title}
                loading="lazy"
                decoding="async"
              />
              <ExhibitionInfo>
                <ExhibitionTitle>{exhibition.title}</ExhibitionTitle>
                <ExhibitionDescription>{exhibition.description}</ExhibitionDescription>
              </ExhibitionInfo>
            </ExhibitionCard>
          ))}
        </ExhibitionGrid>
      </ContentWrapper>
    </Container>
  );
};

export default Exhibitions; 